import { ChangeEvent, useState } from 'react'

import {
  Box,
  Grid,
  SearchField,
  WizardStepRenderParams,
} from '@factorialco/deprecated-design-system'
import { BankingInstitutionsDocument, InstitutionFragment } from 'generated/resources/graphql'

import Select from 'modules/core/components/Select'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import countryList from 'modules/core/lib/countryList'
import i18n from 'modules/core/lib/i18n'

import { InstitutionCard } from 'modules/banking/components/InstitutionCard'
import { ManualInstitutionCard } from 'modules/banking/components/InstitutionCard/manual'

import { WizardAccountData } from '../../types'
import { WizardStepLayout } from '../WizardStepLayout'

interface Props {
  onSelect: (institution: InstitutionFragment | null) => void
}

export const BankAccountSelectorWizardStep = <T extends WizardAccountData>({
  data,
  onDataChanged,
  onSelect,
}: WizardStepRenderParams<T> & Props) => {
  const [country, setCountry] = useState<string | undefined>(undefined)
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const countrySelected = country ?? data.country

  const { data: institutions, loading } = useQuery(BankingInstitutionsDocument, {
    variables: {
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      country: country ? country : countrySelected!,
      name: debouncedSearch || data.search,
    },
    skip: !country && !countrySelected,
  })

  const nodes = useUnsafeMemo(
    () => institutions?.banking.institutionsConnection.nodes,
    [institutions]
  )

  const countryChange = (value: string) => {
    setCountry(value.toUpperCase())
    onDataChanged({
      ...data,
      country: value.toUpperCase(),
    })
  }

  const searchChange = (ev: ChangeEvent<HTMLInputElement>) => {
    onSearch(ev)
    onDataChanged({
      ...data,
      search: ev.target.value,
    })
  }

  return (
    <WizardStepLayout
      title={i18n.t('banking.accounts.new_wizard.step_2.title')}
      subtitle={i18n.t('banking.accounts.new_wizard.step_2.subtitle')}
    >
      <Box style={{ width: '500px' }} height="full" flexDirection="row" gap="s16">
        <Box height="full" style={{ width: '500px' }} flexNoShrink flexDirection="column">
          <Box flexDirection="column" gap="s16">
            <Grid variant="fixed">
              <Box gridColSpan={{ l: 4, m: 4, s: 4, xs: 4, xxs: 4, xl: 4, xxl: 4 }}>
                <Select
                  readonly={loading}
                  none={i18n.t('common.select')}
                  onChange={countryChange}
                  options={countryList}
                  defaultValue={countrySelected?.toLowerCase()}
                />
              </Box>
              <Box gridColSpan={{ l: 8, m: 8, s: 8, xs: 8, xxs: 8, xl: 8, xxl: 8 }}>
                <SearchField
                  disabled={!countrySelected}
                  variant="big"
                  onSearch={searchChange}
                  placeholder={i18n.t('common.search')}
                  search={(search || data.search) ?? ''}
                />
              </Box>
            </Grid>
            <Box style={{ height: '340px' }} overflowY="auto">
              <Grid variant="fixed">
                <ManualInstitutionCard
                  selected={data.institution === undefined}
                  onClick={() => {
                    onSelect(null)
                    onDataChanged({
                      ...data,
                      institution: undefined,
                    })
                  }}
                />
                {nodes &&
                  nodes.map((institution) => (
                    <InstitutionCard
                      key={institution.id}
                      onSelect={() => {
                        onSelect(institution)
                        onDataChanged({
                          ...data,
                          institution,
                        })
                      }}
                      selected={data.institution?.id === institution.id}
                      institution={institution}
                    />
                  ))}
              </Grid>
            </Box>
          </Box>
        </Box>
      </Box>
    </WizardStepLayout>
  )
}
